import type { DecoratedLead } from '@witmetrics/utils';
import { isBlank } from '@witmetrics/utils/strings';
import {
  useActivePractice,
  useDispatch,
  usePracticeProperties,
} from '@witmetrics/data-store';
import { API } from '@/api';
import { showErrorToaster, showSuccessToaster } from '@/utils/toasters';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useString } from '@/hooks/useString';
import { useInvalidFields } from '@/hooks/useInvalidFields';
import TextField from '@/components/inputs/TextField';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import { buildLeadProperties, classes } from './utils';

export type NewPatientDialog = {
  initialFirstname?: string;
  initialLastname?: string;
  onSelect?: (patient: DecoratedLead) => void;
  onClose: () => void;
};

export default function NewPatientDialog({
  initialFirstname = '',
  initialLastname = '',
  onSelect,
  onClose,
}: NewPatientDialog) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const practiceProperties = usePracticeProperties();
  const activePractice = useActivePractice();
  const [firstname, setFirstname] = useString(initialFirstname);
  const [lastname, setLastname] = useString(initialLastname);
  const [externalRef, setExternalRef] = useString();
  const [email, setEmail] = useString();
  const [phone, setPhone] = useString();
  const { setInvalidFields, isValidField } = useInvalidFields();
  const [isSaving, toggleSaving] = useToggle();

  const handleSave = async () => {
    try {
      toggleSaving(true);
      if (isBlank(firstname)) {
        toggleSaving(false);
        return setInvalidFields(['firstname']);
      }
      if (!isBlank(email)) {
        const emailData = await API.Users.validateEmail(email);
        if (!emailData.isValid) {
          showErrorToaster(emailData.message);
          toggleSaving(false);
          return setInvalidFields(['email']);
        }
      }
      return handleCreatePatient();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving patient', () => handleSave());
    }
  };

  const handleCreatePatient = async () => {
    try {
      if (!activePractice) return;
      const properties = buildLeadProperties({ practiceProperties, phone });
      const patient = await API.Leads.createNewLead({
        firstname,
        lastname,
        email,
        externalRef,
        properties,
        practiceId: activePractice.id,
      });
      dispatch.addLeads([patient]);
      showSuccessToaster('Patient created');
      toggleSaving(false);
      onSelect?.(patient);
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error creating patient', () => handleCreatePatient());
    }
  };

  const isSaveDisabled = () => {
    return isSaving || isBlank(firstname);
  };

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle title="New Patient" onClose={onClose} />
      <DialogBody className={classes.body}>
        <div className={classes.section}>
          <TextField
            autoFocus
            error={!isValidField('firstname')}
            className={classes.input}
            label="First name"
            value={firstname}
            onChange={setFirstname}
          />
          <div className={classes.optional}>Optional fields</div>
          <TextField
            className={classes.input}
            label="Last name"
            value={lastname}
            onChange={setLastname}
          />
          <TextField
            className={classes.input}
            label="ID / External Reference"
            value={externalRef}
            onChange={setExternalRef}
          />
          <TextField
            error={!isValidField('email')}
            className={classes.input}
            label="Email"
            value={email}
            onChange={setEmail}
          />
          <TextField
            className={classes.input}
            label="Phone"
            value={phone}
            onChange={setPhone}
          />
        </div>
      </DialogBody>
      <DefaultDialogActions
        disabled={isSaveDisabled()}
        pending={isSaving}
        confirmLabel="Save"
        pendingLabel="Saving"
        onCancel={onClose}
        onConfirm={handleSave}
      />
    </Dialog>
  );
}
